import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import ExcelExporter from './excel-export/index.js';

// 为了向后兼容，提供一个简单的包装类
class ExcelExport {
  // 数据清理函数，移除可能导致ExcelJS出错的特殊方法
  static cleanDataForExcel(data, visited = new WeakMap()) {
    if (data === null || data === undefined) {
      return '';
    }
    
    if (typeof data === 'object') {
      // 处理循环引用
      if (visited.has(data)) {
        return '[Circular Reference]';
      }
      visited.set(data, true);
      
      // 检查是否有equivalentTo方法（包括原型链上的），如果有则需要特殊处理
      if (data.equivalentTo && typeof data.equivalentTo === 'function') {
        // 如果是具有数值的对象，尝试获取其数值表示
        return data.valueOf ? data.valueOf() : String(data);
      }
      
      // 如果是数组，递归处理每一项
      if (Array.isArray(data)) {
        const result = [];
        for (const item of data) {
          // 检查数组项是否有equivalentTo方法
          if (item && typeof item === 'object' && 
              (item.equivalentTo && typeof item.equivalentTo === 'function')) {
            result.push(item.valueOf ? item.valueOf() : String(item));
          } else {
            result.push(this.cleanDataForExcel(item, visited));
          }
        }
        return result;
      }
      
      // 如果是一般对象，递归处理每个属性
      const cleaned = {};
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'function') {
          continue; // 跳过函数
        }
        cleaned[key] = this.cleanDataForExcel(value, visited);
      }
      return cleaned;
    }
    
    return data;
  }
  
  // 获取默认边框样式
  static getDefaultBorder() {
    return {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }
  
  // 导出基础参数设置
  static exportGlobalParams(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const params = projectData["基础数据"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("基础参数设置");

    // 设置列宽
    worksheet.columns = [
      { header: "参数名称", width: 25 },
      { header: "参数值", width: 25 },
    ];

    // 添加标题行
    worksheet.mergeCells("A1:B1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "基础参数设置";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 设置标题行高
    worksheet.getRow(1).height = 40;

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["参数名称", "参数值"];
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 2; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 参数数据
    const paramsData = [
      ["项目名称", projectData.projectName || ""],
      ["建设投资", params.constructionInvestment || 0],
      ["计算期", Math.round(params.calculationPeriod || 0)],
      ["建设期", Math.round(params.constructionPeriod || 0)],
      ["运营期", Math.round(params.operationPeriod || 0)],
      ["正常年份", Math.round(params.normalYear || 0)],
      ["基准收益率", `${(params.benchmarkYield || 0).toFixed(2)}%`],
      ["折现率", `${(params.discountRate || 0).toFixed(2)}%`],
      ["增值税率", `${(params.vatRate || 0).toFixed(2)}%`],
      ["所得税率", `${(params.incomeTaxRate || 0).toFixed(2)}%`],
      ["行业基准收益率", `${(params.industryBenchmarkYield || 0).toFixed(2)}%`],
      ["贷款利率", `${(params.loanRate || 0).toFixed(2)}%`],
      ["残值率", `${(params.salvageRate || 0).toFixed(2)}%`],
      ["折旧年限", Math.round(params.depreciationPeriod || 0)],
      ["摊销年限", Math.round(params.amortizationPeriod || 0)],
      ["经营负荷", `${(params.operatingLoad || 0).toFixed(2)}%`]
    ];

    // 添加数据行
    paramsData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
      row.height = 22;

      // 参数名称列
      const nameCell = row.getCell(1);
      nameCell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      nameCell.alignment = {
        vertical: "middle",
      };
      nameCell.border = this.getDefaultBorder();

      // 参数值列
      const valueCell = row.getCell(2);
      valueCell.font = {
        name: "Microsoft YaHei",
        size: 12,
      };
      valueCell.alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      valueCell.border = this.getDefaultBorder();
    });

    // 为数值列设置数值格式
    for (let i = 0; i < paramsData.length; i++) {
      const valueCell = worksheet.getRow(i + 3).getCell(2);
      const value = paramsData[i][1];
      if (typeof value === 'number' && !value.toString().includes('%')) {
        valueCell.numFmt = '#,##0.00';
      }
    }
    
    return worksheet;
  }
  
  // 导出总投资估算表
  static exportInvestmentEstimate(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const investData = projectData["总投资估算"] || {};
    const totalTable = investData.总表 || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("总投资估算表");

    // 设置列宽
    worksheet.columns = [
      { header: "项目", width: 30 },
      { header: "建筑费用", width: 15 },
      { header: "设备费用", width: 15 },
      { header: "安装费用", width: 15 },
      { header: "其他费用", width: 15 },
      { header: "合计", width: 15 },
      { header: "比例", width: 15 },
    ];

    // 添加标题行
    worksheet.mergeCells("A1:G1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "总投资估算表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 设置标题行高
    worksheet.getRow(1).height = 40;

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["项目", "建筑费用", "设备费用", "安装费用", "其他费用", "合计", "比例"];
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 7; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 数据行
    const tableData = [
      ["建设投资", 
       totalTable.建设投资?.建筑费用 || 0, 
       totalTable.建设投资?.设备费用 || 0, 
       totalTable.建设投资?.安装费用 || 0, 
       totalTable.建设投资?.其他费用 || 0, 
       totalTable.建设投资?.合计 || 0, 
       (totalTable.建设投资?.比例 || 0) * 100 + '%'],
      ["其中：工程费用", 
       totalTable.建设投资?.工程费用?.建筑费用 || 0, 
       totalTable.建设投资?.工程费用?.设备费用 || 0, 
       totalTable.建设投资?.工程费用?.安装费用 || 0, 
       totalTable.建设投资?.工程费用?.其他费用 || 0, 
       totalTable.建设投资?.工程费用?.合计 || 0, 
       ""],
      ["      建筑工程费", 
       totalTable.建设投资?.工程费用?.建筑工程费?.建筑费用 || 0, 
       totalTable.建设投资?.工程费用?.建筑工程费?.设备费用 || 0, 
       totalTable.建设投资?.工程费用?.建筑工程费?.安装费用 || 0, 
       totalTable.建设投资?.工程费用?.建筑工程费?.其他费用 || 0, 
       totalTable.建设投资?.工程费用?.建筑工程费?.合计 || 0, 
       ""],
      ["      设备购置费", 
       totalTable.建设投资?.工程费用?.设备购置费?.建筑费用 || 0, 
       totalTable.建设投资?.工程费用?.设备购置费?.设备费用 || 0, 
       totalTable.建设投资?.工程费用?.设备购置费?.安装费用 || 0, 
       totalTable.建设投资?.工程费用?.设备购置费?.其他费用 || 0, 
       totalTable.建设投资?.工程费用?.设备购置费?.合计 || 0, 
       ""],
      ["      安装工程费", 
       totalTable.建设投资?.工程费用?.安装工程费?.建筑费用 || 0, 
       totalTable.建设投资?.工程费用?.安装工程费?.设备费用 || 0, 
       totalTable.建设投资?.工程费用?.安装工程费?.安装费用 || 0, 
       totalTable.建设投资?.工程费用?.安装工程费?.其他费用 || 0, 
       totalTable.建设投资?.工程费用?.安装工程费?.合计 || 0, 
       ""],
      ["      工程建设其他费用", 
       totalTable.建设投资?.工程建设其他费用?.建筑费用 || 0, 
       totalTable.建设投资?.工程建设其他费用?.设备费用 || 0, 
       totalTable.建设投资?.工程建设其他费用?.安装费用 || 0, 
       totalTable.建设投资?.工程建设其他费用?.其他费用 || 0, 
       totalTable.建设投资?.工程建设其他费用?.合计 || 0, 
       ""],
      ["      预备费", 
       totalTable.建设投资?.预备费?.建筑费用 || 0, 
       totalTable.建设投资?.预备费?.设备费用 || 0, 
       totalTable.建设投资?.预备费?.安装费用 || 0, 
       totalTable.建设投资?.预备费?.其他费用 || 0, 
       totalTable.建设投资?.预备费?.合计 || 0, 
       ""],
      ["流动资金", 
       totalTable.流动资金?.建筑费用 || 0, 
       totalTable.流动资金?.设备费用 || 0, 
       totalTable.流动资金?.安装费用 || 0, 
       totalTable.流动资金?.其他费用 || 0, 
       totalTable.流动资金?.合计 || 0, 
       (totalTable.流动资金?.比例 || 0) * 100 + '%'],
      ["建设期利息", 
       totalTable.建设期利息?.建筑费用 || 0, 
       totalTable.建设期利息?.设备费用 || 0, 
       totalTable.建设期利息?.安装费用 || 0, 
       totalTable.建设期利息?.其他费用 || 0, 
       totalTable.建设期利息?.合计 || 0, 
       (totalTable.建设期利息?.比例 || 0) * 100 + '%'],
      ["总投资", 
       totalTable.总投资?.建筑费用 || 0, 
       totalTable.总投资?.设备费用 || 0, 
       totalTable.总投资?.安装费用 || 0, 
       totalTable.总投资?.其他费用 || 0, 
       totalTable.总投资?.合计 || 0, 
       (totalTable.总投资?.比例 || 0) * 100 + '%'],
    ];

    // 添加数据行
    tableData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
      row.height = 22;

      // 项目名称列
      const nameCell = row.getCell(1);
      nameCell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: index < 9, // 前几行加粗
      };
      nameCell.alignment = {
        vertical: "middle",
      };
      nameCell.border = this.getDefaultBorder();

      // 数据列
      for (let col = 2; col <= 6; col++) {
        const dataCell = row.getCell(col);
        dataCell.font = {
          name: "Microsoft YaHei",
          size: 12,
        };
        dataCell.alignment = {
          vertical: "middle",
          horizontal: "right",
        };
        dataCell.border = this.getDefaultBorder();
        
        // 为数值列设置数值格式
        if (col < 6 && typeof rowData[col] === 'number') {
          dataCell.numFmt = '#,##0.00';
        }
      }

      // 比例列特殊处理
      const ratioCell = row.getCell(7);
      ratioCell.font = {
        name: "Microsoft YaHei",
        size: 12,
      };
      ratioCell.alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      ratioCell.border = this.getDefaultBorder();
      if (typeof rowData[6] === 'string' && rowData[6].includes('%')) {
        ratioCell.numFmt = '0.00%';
      }
    });
    
    return worksheet;
  }
  
  // 导出资金筹措
  static exportFundingPlan(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const fundingData = projectData["资金筹措及分年投资计划"] || {};
    const financingData = fundingData["资金筹措"] || {};
    const debtData = financingData.债务资金 || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("资金筹措");

    // 设置列宽
    const columns = [
      { header: "项目", width: 25 },
      { header: "金额（万元）", width: 15 },
    ];
    worksheet.columns = columns;

    // 添加表格标题
    worksheet.mergeCells("A1:B1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "资金筹措";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["项目", "金额（万元）"];
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 2; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 添加数据行
    const tableData = [
      ["资金筹措总额", financingData.amount || 0],
      ["项目资本金", financingData.项目资本金?.amount || 0],
      ["资本金比例", financingData.项目资本金?.proportion || 0],
      ["债务资金", financingData.债务资金?.amount || 0],
      ["债务资金比例", financingData.债务资金?.proportion || 0],
      ["银行借款", debtData.银行借款?.amount || 0],
      ["银行借款利率", debtData.银行借款?.rate || 0],
      ["债券", debtData.债券?.amount || 0],
      ["债券利率", debtData.债券?.rate || 0],
      ["融资租赁", debtData.融资租赁?.amount || 0],
      ["融资租赁利率", debtData.融资租赁?.rate || 0],
      ["其他借款", debtData.其他借款?.amount || 0],
      ["其他借款利率", debtData.其他借款?.rate || 0],
    ];

    tableData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
      row.height = 22;

      // 项目名称列
      const nameCell = row.getCell(1);
      nameCell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      nameCell.alignment = {
        vertical: "middle",
      };
      nameCell.border = this.getDefaultBorder();

      // 金额列
      const amountCell = row.getCell(2);
      amountCell.font = {
        name: "Microsoft YaHei",
        size: 12,
      };
      amountCell.alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      amountCell.border = this.getDefaultBorder();
      
      // 设置数值格式
      if (typeof rowData[1] === 'number') {
        amountCell.numFmt = '#,##0.00';
      }
    });

    return worksheet;
  }

  // 导出营业收入税金及利润汇总表
  static exportRevenueTaxSummary(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const revenueData = projectData["营业收入税金及利润"] || {};
    const profitData = revenueData["利润"] || {};
    const taxData = revenueData["营业收入及营业税金"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;

    // 创建工作表
    const worksheet = workbook.addWorksheet("营业收入税金及利润");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 30 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 12 });
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "营业收入税金及利润";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 2 + calculationPeriod; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 辅助函数：获取年份数据
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 辅助函数：添加行
    const addRow = (seq, name, dataObj, yearKey, totalKey, isBold = false) => {
      const row = worksheet.getRow(currentRow++);
      const yearData = getYearData(dataObj, yearKey, calculationPeriod);
      const values = [
        seq,
        name,
        dataObj[totalKey] || 0,
        ...yearData,
      ];
      row.values = values;
      row.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: isBold,
      };
      row.alignment = {
        vertical: "middle",
      };
      row.height = 22;

      // 设置边框
      for (let col = 1; col <= 3 + calculationPeriod; col++) {
        const cell = row.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 设置数值列格式
      for (let col = 3; col <= 2 + calculationPeriod; col++) {
        const cell = row.getCell(col);
        cell.alignment = {
          horizontal: "right",
          vertical: "middle",
        };
        cell.numFmt = '#,##0.00';
      }
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 3;

    // 1. 营业收入
    addRow("1", "营业收入", taxData, "revenueByYear", "revenuetotal", true);

    // 2. 营业税金及附加
    addRow("2", "营业税金及附加", taxData, "taxAndLevyByYear", "taxAndLevyTotal", true);

    // 3. 增值税
    addRow("3", "增值税", taxData, "vatByYear", "vatTotal");

    // 4. 营业利润
    addRow("4", "营业利润", profitData, "operatingProfitByYear", "operatingProfitTotal", true);

    // 5. 补贴收入
    addRow("5", "补贴收入", profitData, "subsidyIncomeByYear", "subsidyIncomeTotal");

    // 6. 利润总额
    addRow("6", "利润总额", profitData, "profitBeforeTaxByYear", "profitBeforeTaxTotal", true);

    // 7. 弥补以前年度亏损
    addRow("7", "弥补以前年度亏损", profitData, "lossCompensationByYear", "lossCompensationTotal");

    // 8. 应纳税所得额
    addRow("8", "应纳税所得额", profitData, "taxableIncomeByYear", "taxableIncomeTotal");

    // 9. 所得税
    addRow("9", "所得税", profitData, "incomeTaxByYear", "incomeTaxTotal");

    // 10. 净利润
    addRow("10", "净利润", profitData, "netProfitByYear", "netProfitTotal", true);

    // 11. 期初未分配利润
    addRow("11", "期初未分配利润", profitData, "beginningUndistributedProfitByYear", "beginningUndistributedProfitTotal");

    // 12. 可供分配利润
    addRow("12", "可供分配利润", profitData, "distributableProfitByYear", "distributableProfitTotal", true);

    // 13. 法定盈余公积
    addRow("13", "法定盈余公积", profitData, "statutorySurplusReserveByYear", "statutorySurplusReserveTotal");

    // 14. 任意盈余公积
    addRow("14", "任意盈余公积", profitData, "discretionarySurplusReserveByYear", "discretionarySurplusReserveTotal");

    // 15. 应付优先股股利
    addRow("15", "应付优先股股利", profitData, "preferredDividendByYear", "preferredDividendTotal");

    // 16. 应付普通股股利
    addRow("16", "应付普通股股利", profitData, "commonDividendByYear", "commonDividendTotal");

    // 17. 未分配利润
    addRow("17", "未分配利润", profitData, "undistributedProfitByYear", "undistributedProfitTotal", true);

    // 18. 息税前利润
    addRow("18", "息税前利润", profitData, "ebitByYear", "ebitTotal");

    // 19. 息税折旧摊销前利润
    addRow("19", "息税折旧摊销前利润", profitData, "ebitdaByYear", "ebitdaTotal");

    return worksheet;
  }

  // 导出成本费用
  static exportCostExpenses(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const costData = projectData["成本费用"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 1;
    const operationPeriod = constru.operationPeriod || 14;

    // 原辅材料数据
    const rawMaterialsData = costData["原辅材料"] || {};
    const fuelPowerData = costData["燃料动力"] || {};
    const welfareData = costData["工资福利"] || {};
    const depreciationData = costData["折旧摊销"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("成本费用");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 30 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 12 });
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "成本费用";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 2 + calculationPeriod; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 添加建设期/运营期行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["-", "建设期/运营期", "-"];
    for (let i = 0; i < calculationPeriod; i++) {
      if (i < constructionPeriod) {
        periodValues.push("建设期");
      } else {
        periodValues.push("运营期");
      }
    }
    periodRow.values = periodValues;
    periodRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    periodRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    periodRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    periodRow.height = 25;

    // 辅助函数：获取年份数据，确保长度正确
    const getYearData = (data, key, period) => {
      if (!data || typeof data !== 'object') {
        return Array(period).fill(0);
      }
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 添加数据行
    const materialsList = rawMaterialsData.materials?.materialslist || [];
    let seq = 1;
    let currentRow = 4;

    // 原材料费用分类行
    if (materialsList.length > 0) {
      const materialsRow = worksheet.getRow(currentRow++);
      const materialsYearData = getYearData(
        rawMaterialsData.materials,
        "year",
        calculationPeriod
      );
      const materialsValues = [
        seq++,
        "原材料费用",
        rawMaterialsData.materials?.total || 0,
        ...materialsYearData,
      ];
      materialsRow.values = materialsValues;

      // 添加具体原材料行
      materialsList.forEach((material) => {
        const materialRow = worksheet.getRow(currentRow++);
        const materialValues = [
          material.sequence,
          `${material.name}（${material.unitPrice}元/${
            material.unit || "吨"
          }）`,
          material.total,
          ...(material.year || Array(calculationPeriod).fill(0)),
        ];
        materialRow.values = materialValues;

        // 年耗量行
        const consumptionRow = worksheet.getRow(currentRow++);
        const consumptionValues = [
          "",
          `年耗量（${material.unit}）`,
          material.consumptionTotal,
          ...(material.consumption || Array(calculationPeriod).fill(0)),
        ];
        consumptionRow.values = consumptionValues;

        // 进项税额行
        const taxRow = worksheet.getRow(currentRow++);
        const taxValues = [
          "",
          `进项税额（${(material.taxRate * 100).toFixed(0)}%）`,
          material.taxTotal,
          ...(material.tax || Array(calculationPeriod).fill(0)),
        ];
        taxRow.values = taxValues;
      });
    }

    // 辅助材料费用
    const auxiliaryRow = worksheet.getRow(currentRow++);
    const auxiliaryYearData = getYearData(
      rawMaterialsData.auxiliary,
      "year",
      calculationPeriod
    );
    const auxiliaryValues = [
      seq++,
      "辅助材料费用",
      rawMaterialsData.auxiliary?.total || 0,
      ...auxiliaryYearData,
    ];
    auxiliaryRow.values = auxiliaryValues;

    // 辅助材料进项税额
    const auxiliaryTaxRow = worksheet.getRow(currentRow++);
    const auxiliaryTaxValues = [
      "",
      `进项税额（${(rawMaterialsData.auxiliary?.taxRate * 100).toFixed(0)}%）`,
      rawMaterialsData.auxiliary?.taxTotal || 0,
      ...(rawMaterialsData.auxiliary?.tax || Array(calculationPeriod).fill(0)),
    ];
    auxiliaryTaxRow.values = auxiliaryTaxValues;

    // 其他材料费用
    const otherRow = worksheet.getRow(currentRow++);
    const otherYearData = getYearData(
      rawMaterialsData.other,
      "year",
      calculationPeriod
    );
    const otherValues = [
      seq++,
      "其他材料费用",
      rawMaterialsData.other?.total || 0,
      ...otherYearData,
    ];
    otherRow.values = otherValues;

    // 其他材料进项税额
    const otherTaxRow = worksheet.getRow(currentRow++);
    const otherTaxValues = [
      "",
      `进项税额（${(rawMaterialsData.other?.taxRate * 100).toFixed(0)}%）`,
      rawMaterialsData.other?.taxTotal || 0,
      ...(rawMaterialsData.other?.tax || Array(calculationPeriod).fill(0)),
    ];
    otherTaxRow.values = otherTaxValues;

    // 合计行
    const totalRow = worksheet.getRow(currentRow++);
    const totalYearData = getYearData(
      rawMaterialsData.total,
      "year",
      calculationPeriod
    );
    const totalValues = [
      seq++,
      "外购原辅材料合计",
      rawMaterialsData.total?.total || 0,
      ...totalYearData,
    ];
    totalRow.values = totalValues;

    // 合计进项税额
    const totalTaxRow = worksheet.getRow(currentRow++);
    const totalTaxValues = [
      seq++,
      "进项税额合计",
      rawMaterialsData.total?.taxTotal || 0,
      ...(rawMaterialsData.total?.tax || Array(calculationPeriod).fill(0)),
    ];
    totalTaxRow.values = totalTaxValues;

    // 燃料动力费用
    const fuelPowerList = fuelPowerData.fuelpower || [];
    if (fuelPowerList.length > 0) {
      const fuelPowerRow = worksheet.getRow(currentRow++);
      const fuelPowerYearData = getYearData(
        fuelPowerData.fuelpower,
        "year",
        calculationPeriod
      );
      const fuelPowerValues = [
        seq++,
        "燃料动力费用",
        fuelPowerData.fuelpower?.total || 0,
        ...fuelPowerYearData,
      ];
      fuelPowerRow.values = fuelPowerValues;

      // 添加具体燃料动力行
      fuelPowerList.forEach((fuel) => {
        const fuelRow = worksheet.getRow(currentRow++);
        const fuelValues = [
          fuel.sequence,
          `${fuel.name}（${fuel.unitPrice}元/${fuel.unit || "吨"}）`,
          fuel.total,
          ...(fuel.year || Array(calculationPeriod).fill(0)),
        ];
        fuelRow.values = fuelValues;
      });
    }

    // 工资福利费用
    const welfareRow = worksheet.getRow(currentRow++);
    const welfareYearData = getYearData(
      welfareData.welfare,
      "year",
      calculationPeriod
    );
    const welfareValues = [
      seq++,
      "工资福利",
      welfareData.welfare?.total || 0,
      ...welfareYearData,
    ];
    welfareRow.values = welfareValues;

    // 折旧费用
    const depreciationRow = worksheet.getRow(currentRow++);
    const depreciationYearData = getYearData(
      depreciationData.depreciation,
      "year",
      calculationPeriod
    );
    const depreciationValues = [
      seq++,
      "折旧费",
      depreciationData.depreciation?.total || 0,
      ...depreciationYearData,
    ];
    depreciationRow.values = depreciationValues;

    // 摊销费用
    const amortizationRow = worksheet.getRow(currentRow++);
    const amortizationYearData = getYearData(
      depreciationData.amortization,
      "year",
      calculationPeriod
    );
    const amortizationValues = [
      seq++,
      "摊销费",
      depreciationData.amortization?.total || 0,
      ...amortizationYearData,
    ];
    amortizationRow.values = amortizationValues;

    // 利息支出
    const interestRow = worksheet.getRow(currentRow++);
    const interestYearData = getYearData(
      costData.interest,
      "year",
      calculationPeriod
    );
    const interestValues = [
      seq++,
      "利息支出",
      costData.interest?.total || 0,
      ...interestYearData,
    ];
    interestRow.values = interestValues;

    // 其他费用
    const otherCostsRow = worksheet.getRow(currentRow++);
    const otherCostsYearData = getYearData(
      costData.otherCosts,
      "year",
      calculationPeriod
    );
    const otherCostsValues = [
      seq++,
      "其他费用",
      costData.otherCosts?.total || 0,
      ...otherCostsYearData,
    ];
    otherCostsRow.values = otherCostsValues;

    // 总成本费用
    const totalCostsRow = worksheet.getRow(currentRow++);
    const totalCostsYearData = getYearData(
      costData.totalCosts,
      "year",
      calculationPeriod
    );
    const totalCostsValues = [
      seq++,
      "总成本费用",
      costData.totalCosts?.total || 0,
      ...totalCostsYearData,
    ];
    totalCostsRow.values = totalCostsValues;

    // 设置边框
    for (let rowIndex = 4; rowIndex < currentRow; rowIndex++) {
      const row = worksheet.getRow(rowIndex);
      for (let colIndex = 1; colIndex <= 3 + calculationPeriod; colIndex++) {
        const cell = row.getCell(colIndex);
        cell.border = this.getDefaultBorder();
      }
    }

    return worksheet;
  }

  // 导出盈利能力分析
  static exportProfitabilityAnalysis(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const profitabilityData = projectData["盈利能力分析"] || {};
    const allInvestmentData = profitabilityData["全部投资现金流量"] || {};
    const capitalData = profitabilityData["资本金现金流量"] || {};
    const financialData = profitabilityData["财务计划现金流量"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("盈利能力分析");

    // 设置列宽
    worksheet.columns = [
      { header: "指标", width: 35 },
      { header: "数值", width: 15 },
      { header: "单位", width: 10 },
    ];

    // 添加表格标题
    worksheet.mergeCells("A1:C1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "盈利能力分析";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["指标", "数值", "单位"];
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 3; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 盈利能力分析数据
    const analysisData = [
      ["项目投资财务内部收益率(所得税前)", allInvestmentData.irrBeforeTax || 0, "%"],
      ["项目投资财务内部收益率(所得税后)", allInvestmentData.irrAfterTax || 0, "%"],
      ["项目投资财务净现值(所得税前)", allInvestmentData.npvBeforeTax || 0, "万元"],
      ["项目投资财务净现值(所得税后)", allInvestmentData.npvAfterTax || 0, "万元"],
      ["项目投资投资回收期(所得税前)", allInvestmentData.paybackPeriodBeforeTax || 0, "年"],
      ["项目投资投资回收期(所得税后)", allInvestmentData.paybackPeriodAfterTax || 0, "年"],
      ["资本金财务内部收益率(所得税后)", capitalData.irr || 0, "%"],
      ["资本金财务净现值", capitalData.npv || 0, "万元"],
      ["财务净现值(累计盈余资金)", financialData.npv || 0, "万元"],
      ["资产负债率(%)", profitabilityData.debtToAssetRatio || 0, "%"],
      ["流动比率(%)", profitabilityData.currentRatio || 0, "%"],
      ["速动比率(%)", profitabilityData.quickRatio || 0, "%"]
    ];

    // 添加数据行
    analysisData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
      row.height = 22;

      // 指标列
      const nameCell = row.getCell(1);
      nameCell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      nameCell.alignment = {
        vertical: "middle",
      };
      nameCell.border = this.getDefaultBorder();

      // 数值列
      const valueCell = row.getCell(2);
      valueCell.font = {
        name: "Microsoft YaHei",
        size: 12,
      };
      valueCell.alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      valueCell.border = this.getDefaultBorder();
      valueCell.numFmt = '#,##0.00';

      // 单位列
      const unitCell = row.getCell(3);
      unitCell.font = {
        name: "Microsoft YaHei",
        size: 12,
      };
      unitCell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      unitCell.border = this.getDefaultBorder();
    });

    return worksheet;
  }

  // 导出财务计划现金流量表
  static exportFinancialPlanCashFlow(projectData, workbook) {
    // 清理数据
    projectData = this.cleanDataForExcel(projectData);
    const financialData = projectData["盈利能力分析"]?.["财务计划现金流量表"] || projectData["盈利能力分析"]?.["财务计划现金流量"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;

    // 创建工作表
    const worksheet = workbook.addWorksheet("财务计划现金流量表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 30 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 12 });
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1
    );
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "财务计划现金流量表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    headerRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF34495E" },
    };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE3F2FD" },
    };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    headerRow.height = 30;

    // 设置表头单元格边框
    for (let col = 1; col <= 2 + calculationPeriod; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 辅助函数：获取年份数据，确保长度正确
    const getYearData = (data, key, period) => {
      if (!data || typeof data !== 'object') {
        return Array(period).fill(0);
      }
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 辅助函数：添加行
    const addRow = (seq, name, dataObj, yearKey, totalKey, isBold = false) => {
      const row = worksheet.getRow(currentRow++);
      const yearData = getYearData(dataObj, yearKey, calculationPeriod);
      const values = [
        seq,
        name,
        dataObj && dataObj[totalKey] ? dataObj[totalKey] : 0,
        ...yearData,
      ];
      row.values = values;
      row.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: isBold,
      };
      row.alignment = {
        vertical: "middle",
      };
      row.height = 22;

      // 设置边框
      for (let col = 1; col <= 3 + calculationPeriod; col++) {
        const cell = row.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 设置数值列格式
      for (let col = 3; col <= 2 + calculationPeriod; col++) {
        const cell = row.getCell(col);
        cell.alignment = {
          horizontal: "right",
          vertical: "middle",
        };
        cell.numFmt = '#,##0.00';
      }
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 3;

    // 现金流入
    addRow("1", "现金流入", financialData.cashInflow, "totalByYear", "total", true);

    // 销售收入
    addRow("1.1", "销售收入", financialData.cashInflow, "salesByYear", "salesTotal");

    // 补贴收入
    addRow("1.2", "补贴收入", financialData.cashInflow, "subsidyByYear", "subsidyTotal");

    // 回收资产
    addRow("1.3", "回收资产", financialData.cashInflow, "recycleAssetsByYear", "recycleAssetsTotal");

    // 现金流出
    addRow("2", "现金流出", financialData.cashOutflow, "totalByYear", "total", true);

    // 建设投资
    addRow("2.1", "建设投资", financialData.cashOutflow, "investmentByYear", "investmentTotal");

    // 流动资金
    addRow("2.2", "流动资金", financialData.cashOutflow, "workingCapitalByYear", "workingCapitalTotal");

    // 经营成本
    addRow("2.3", "经营成本", financialData.cashOutflow, "operatingCostByYear", "operatingCostTotal");

    // 营业税金及附加
    addRow("2.4", "营业税金及附加", financialData.cashOutflow, "taxAndLevyByYear", "taxAndLevyTotal");

    // 所得税
    addRow("2.5", "所得税", financialData.cashOutflow, "incomeTaxByYear", "incomeTaxTotal");

    // 现金净流量
    addRow("3", "现金净流量", financialData.netCashFlow, "byYear", "total", true);

    // 累计现金净流量
    addRow("4", "累计现金净流量", financialData.cumulativeNetCash, "byYear", "total");

    // 财务净现值
    addRow("5", "财务净现值", financialData.npv, "byYear", "total");

    return worksheet;
  }

  static async exportProject(projectData, toast, chartInstances = {}) {
    return await ExcelExporter.exportProject(projectData, toast, chartInstances);
  }
}

export default ExcelExport;
